/**
 * HOTTR Passport SSO (same contract as HOTTR.tv).
 * client_id: hottr-me — must exist in passport_clients with allowed redirect_uris.
 */

const PASSPORT_URL = (import.meta.env.VITE_PASSPORT_URL as string | undefined)?.replace(
  /\/$/,
  '',
);
const CLIENT_ID = 'hottr-me';

export function isPassportEnabled(): boolean {
  return Boolean(PASSPORT_URL);
}

export function passportPublicUrl(): string | null {
  return PASSPORT_URL || null;
}

function b64url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function sha256(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return b64url(hash);
}

function randomString(len = 43): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  const arr = new Uint8Array(len);
  crypto.getRandomValues(arr);
  return Array.from(arr, (n) => chars[n % chars.length]).join('');
}

/** Start SSO: store PKCE, redirect to Passport. */
export async function beginPassportLogin(returnTo = '/today'): Promise<void> {
  if (!PASSPORT_URL) throw new Error('Account sign-in isn’t available right now.');

  const verifier = randomString(64);
  const challenge = await sha256(verifier);
  const state = randomString(24);
  const redirectUri = `${window.location.origin}/auth/callback`;
  const safeReturn =
    returnTo.startsWith('/') && !returnTo.startsWith('//') ? returnTo : '/today';

  sessionStorage.setItem('hottr.passport.verifier', verifier);
  sessionStorage.setItem('hottr.passport.state', state);
  sessionStorage.setItem('hottr.passport.returnTo', safeReturn);
  sessionStorage.setItem('hottr.passport.redirectUri', redirectUri);
  sessionStorage.setItem('hottr.passport.clientId', CLIENT_ID);
  sessionStorage.setItem('hottr.passport.origin', window.location.origin);

  const url = new URL(`${PASSPORT_URL}/sso`);
  url.searchParams.set('client_id', CLIENT_ID);
  url.searchParams.set('redirect_uri', redirectUri);
  url.searchParams.set('state', state);
  url.searchParams.set('code_challenge', challenge);
  url.searchParams.set('code_challenge_method', 'S256');
  url.searchParams.set('app', 'HOTTR.me');
  window.location.assign(url.toString());
}

export type PassportExchangeResult = {
  ok: true;
  user: { id: string; email?: string };
  destination?: {
    project_url: string;
    email: string;
    email_otp: string | null;
  } | null;
  passport?: {
    project_url: string;
    anon_key: string;
    email: string;
    email_otp: string | null;
  };
};

/** Complete SSO after /auth/callback?code=&state= */
export async function completePassportLogin(
  code: string,
  state: string,
): Promise<PassportExchangeResult> {
  const savedState = sessionStorage.getItem('hottr.passport.state');
  const verifier = sessionStorage.getItem('hottr.passport.verifier');
  const redirectUri =
    sessionStorage.getItem('hottr.passport.redirectUri') ||
    `${window.location.origin}/auth/callback`;

  if (!verifier || !savedState || savedState !== state) {
    throw new Error('Invalid SSO state — try signing in again.');
  }
  if (!PASSPORT_URL) throw new Error('Account sign-in isn’t available right now.');

  const res = await fetch(`${PASSPORT_URL}/.netlify/functions/sso-exchange`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      code,
      code_verifier: verifier,
      client_id: CLIENT_ID,
      redirect_uri: redirectUri,
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.message || data.error || `SSO exchange failed (${res.status})`);
  }

  sessionStorage.removeItem('hottr.passport.verifier');
  sessionStorage.removeItem('hottr.passport.state');

  return data as PassportExchangeResult;
}

export function consumePassportReturnTo(): string {
  const t = sessionStorage.getItem('hottr.passport.returnTo') || '/today';
  sessionStorage.removeItem('hottr.passport.returnTo');
  return t;
}
